import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Bot, MapPin, ArrowRight, Building2, Users, Globe,
  TrendingUp, Check, X
} from 'lucide-react';
import { PublicHeader } from '@/components/common/PublicHeader';
import { PublicFooter } from '@/components/common/PublicFooter';

const LOCATIONS = [
  // United States
  { id: 'new-york', city: 'New York', state: 'NY', businesses: '230K+', region: 'Northeast' },
  { id: 'los-angeles', city: 'Los Angeles', state: 'CA', businesses: '180K+', region: 'West Coast' },
  { id: 'chicago', city: 'Chicago', state: 'IL', businesses: '95K+', region: 'Midwest' },
  { id: 'houston', city: 'Houston', state: 'TX', businesses: '85K+', region: 'South' },
  { id: 'phoenix', city: 'Phoenix', state: 'AZ', businesses: '72K+', region: 'Southwest' },
  { id: 'philadelphia', city: 'Philadelphia', state: 'PA', businesses: '68K+', region: 'Northeast' },
  { id: 'san-antonio', city: 'San Antonio', state: 'TX', businesses: '52K+', region: 'South' },
  { id: 'san-diego', city: 'San Diego', state: 'CA', businesses: '58K+', region: 'West Coast' },
  { id: 'dallas', city: 'Dallas', state: 'TX', businesses: '65K+', region: 'South' },
  { id: 'san-jose', city: 'San Jose', state: 'CA', businesses: '45K+', region: 'West Coast' },
  { id: 'austin', city: 'Austin', state: 'TX', businesses: '48K+', region: 'South' },
  { id: 'san-francisco', city: 'San Francisco', state: 'CA', businesses: '55K+', region: 'West Coast' },
  { id: 'miami', city: 'Miami', state: 'FL', businesses: '45K+', region: 'Southeast' },
  { id: 'atlanta', city: 'Atlanta', state: 'GA', businesses: '55K+', region: 'Southeast' },
  { id: 'seattle', city: 'Seattle', state: 'WA', businesses: '48K+', region: 'Pacific Northwest' },
  { id: 'denver', city: 'Denver', state: 'CO', businesses: '42K+', region: 'Mountain West' },
  { id: 'boston', city: 'Boston', state: 'MA', businesses: '38K+', region: 'Northeast' },
  { id: 'nashville', city: 'Nashville', state: 'TN', businesses: '32K+', region: 'South' },
  { id: 'portland', city: 'Portland', state: 'OR', businesses: '35K+', region: 'Pacific Northwest' },
  { id: 'las-vegas', city: 'Las Vegas', state: 'NV', businesses: '38K+', region: 'Southwest' },
  { id: 'detroit', city: 'Detroit', state: 'MI', businesses: '28K+', region: 'Midwest' },
  { id: 'minneapolis', city: 'Minneapolis', state: 'MN', businesses: '30K+', region: 'Midwest' },
  { id: 'charlotte', city: 'Charlotte', state: 'NC', businesses: '34K+', region: 'Southeast' },
  { id: 'tampa', city: 'Tampa', state: 'FL', businesses: '36K+', region: 'Southeast' },
  { id: 'orlando', city: 'Orlando', state: 'FL', businesses: '32K+', region: 'Southeast' },
  { id: 'sacramento', city: 'Sacramento', state: 'CA', businesses: '28K+', region: 'West Coast' },
  { id: 'pittsburgh', city: 'Pittsburgh', state: 'PA', businesses: '22K+', region: 'Northeast' },
  { id: 'cincinnati', city: 'Cincinnati', state: 'OH', businesses: '24K+', region: 'Midwest' },
  { id: 'cleveland', city: 'Cleveland', state: 'OH', businesses: '22K+', region: 'Midwest' },
  { id: 'kansas-city', city: 'Kansas City', state: 'MO', businesses: '26K+', region: 'Midwest' },
  // International
  { id: 'london', city: 'London', state: 'UK', businesses: '400K+', region: 'Europe' },
  { id: 'toronto', city: 'Toronto', state: 'Canada', businesses: '180K+', region: 'North America' },
  { id: 'sydney', city: 'Sydney', state: 'Australia', businesses: '250K+', region: 'Asia Pacific' },
  { id: 'dubai', city: 'Dubai', state: 'UAE', businesses: '300K+', region: 'Middle East' },
  { id: 'singapore', city: 'Singapore', state: 'Singapore', businesses: '280K+', region: 'Asia Pacific' },
  { id: 'melbourne', city: 'Melbourne', state: 'Australia', businesses: '220K+', region: 'Asia Pacific' },
  { id: 'vancouver', city: 'Vancouver', state: 'Canada', businesses: '130K+', region: 'North America' },
  { id: 'amsterdam', city: 'Amsterdam', state: 'Netherlands', businesses: '90K+', region: 'Europe' },
  { id: 'frankfurt', city: 'Frankfurt', state: 'Germany', businesses: '75K+', region: 'Europe' },
  { id: 'mumbai', city: 'Mumbai', state: 'India', businesses: '500K+', region: 'Asia Pacific' },
];

const REGIONS = ['Northeast', 'West Coast', 'Midwest', 'South', 'Southeast', 'Southwest', 'Pacific Northwest', 'Mountain West', 'Europe', 'Asia Pacific', 'Middle East', 'North America'];

const LocationsPage = () => {
  const navigate = useNavigate();
  const [activeRegion, setActiveRegion] = useState(null);
  const gridRef = useRef(null);

  const visibleLocations = activeRegion
    ? LOCATIONS.filter(l => l.region === activeRegion)
    : LOCATIONS;

  const selectRegion = (region) => {
    setActiveRegion(prev => (prev === region ? null : region));
    // Scroll the cities grid into view
    setTimeout(() => {
      if (gridRef.current) {
        const y = gridRef.current.getBoundingClientRect().top + window.pageYOffset - 90;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }, 80);
  };

  return (
    <>
      <Helmet>
        <title>AI Receptionist by City | Harmonix AI Employee</title>
        <meta name="description" content="Find AI receptionist and virtual assistant services in your city. Harmonix serves businesses across the United States with 24/7 automated customer service." />
        <meta name="keywords" content="AI receptionist, virtual assistant, business automation, answering service, local AI services" />
        <link rel="canonical" href="https://harmonix.info/locations" />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <PublicHeader />
        
        {/* Hero */}
        <section className="py-16 sm:py-20 px-4 sm:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-900 rounded-full text-sm font-medium mb-6">
              <Globe className="w-4 h-4" />
              Serving Businesses Worldwide
            </div>
            
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
              AI Receptionist in Your City
            </h1>
            
            <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
              Businesses across the US and around the world trust Harmonix to handle customer calls, book appointments, and capture leads 24/7.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4 text-sm text-slate-600">
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-emerald-600" />
                <span>24/7 Availability</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-emerald-600" />
                <span>Local Time Zone Support</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-emerald-600" />
                <span>Industry-Specific AI</span>
              </div>
            </div>
          </div>
        </section>
        
        {/* Locations Grid */}
        <section className="py-12 px-4 sm:px-6 bg-slate-50" ref={gridRef}>
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mb-8">
              <h2 className="text-2xl font-bold text-slate-900">
                {activeRegion ? `Cities in ${activeRegion}` : 'Select Your City'}
              </h2>
              {activeRegion && (
                <button
                  onClick={() => setActiveRegion(null)}
                  className="inline-flex items-center gap-1.5 text-sm text-teal-700 hover:text-teal-800 font-semibold"
                  data-testid="clear-region-filter"
                >
                  <X className="w-3.5 h-3.5" /> Show all cities ({LOCATIONS.length})
                </button>
              )}
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {visibleLocations.map((location) => (
                <Link
                  key={location.id}
                  to={`/ai-receptionist/${location.id}`}
                  className="group"
                  data-testid={`location-card-${location.id}`}
                >
                  <Card className="bg-white border-slate-200 hover:border-teal-300 hover:shadow-lg transition-all h-full">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-600 to-cyan-600 flex items-center justify-center">
                          <MapPin className="w-6 h-6 text-white" />
                        </div>
                        <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-teal-600 transition-colors" />
                      </div>
                      
                      <h3 className="text-xl font-semibold text-slate-900 mb-1">
                        {location.city}, {location.state}
                      </h3>
                      
                      <p className="text-sm text-slate-500 mb-4">{location.region}</p>
                      
                      <div className="flex items-center gap-2 text-sm">
                        <Building2 className="w-4 h-4 text-emerald-600" />
                        <span className="text-slate-600">{location.businesses} businesses</span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>

            {visibleLocations.length === 0 && (
              <p className="text-center text-slate-500 mt-8">No cities found in {activeRegion}.</p>
            )}
          </div>
        </section>
        
        {/* By Region — clickable chips filter the grid above */}
        <section className="py-12 px-4 sm:px-6">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl font-bold text-slate-900 text-center mb-3">
              Browse by Region
            </h2>
            <p className="text-center text-sm text-slate-500 mb-8">Click a region to filter the city list above.</p>

            <div className="flex flex-wrap justify-center gap-3">
              {REGIONS.map((region) => {
                const count = LOCATIONS.filter(l => l.region === region).length;
                if (count === 0) return null;
                const isActive = activeRegion === region;
                return (
                  <button
                    key={region}
                    onClick={() => selectRegion(region)}
                    data-testid={`region-chip-${region.replace(/\s+/g, '-').toLowerCase()}`}
                    className={`px-4 py-2 rounded-full font-medium transition-all ${
                      isActive
                        ? 'bg-teal-600 text-white shadow-md shadow-teal-200'
                        : 'bg-slate-100 text-slate-700 hover:bg-teal-50 hover:text-teal-700 hover:-translate-y-0.5 border border-transparent hover:border-teal-200'
                    }`}
                  >
                    {region} <span className="ml-1 text-xs opacity-70">({count})</span>
                  </button>
                );
              })}
            </div>
          </div>
        </section>
        
        {/* Stats */}
        <section className="py-16 bg-teal-600 px-4 sm:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid sm:grid-cols-3 gap-8 text-center">
              <div>
                <p className="text-4xl font-bold text-white mb-2">50,000+</p>
                <p className="text-blue-100">Businesses Served</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-white mb-2">2M+</p>
                <p className="text-blue-100">Conversations Handled</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-white mb-2">40+</p>
                <p className="text-blue-100">Cities Worldwide</p>
              </div>
            </div>
          </div>
        </section>
        
        {/* CTA */}
        <section className="py-16 px-4 sm:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Don't See Your City?
            </h2>
            <p className="text-xl text-slate-600 mb-8">
              Harmonix works for businesses everywhere. Start your free trial today.
            </p>
            <Button 
              onClick={() => navigate('/login?signup=true')} 
              size="lg" 
              className="bg-teal-600 hover:bg-teal-700 text-lg px-8"
            >
              Get Started Free
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </section>
        
        <PublicFooter />
      </div>
    </>
  );
};

export default LocationsPage;
